import React, { useEffect, useContext, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import moment from 'moment'
import { AuthFormValues, RequestDetail, RequestItem, SupportingDocs } from 'types';
import { subscriptionSelector } from 'store/reducers/subscription'
import { cookies } from 'store/apis'

import { HistoryContext } from 'components/contexts';
import { Topography } from '../Topography/Topography';
import { StatusMarker } from '../StatusMarker/StatusMarker';

import styles from './RequestCard.module.scss';
import { getContodianDetails } from 'store/actions/favouriteCustodians.action';
import { setEstimateAuth as setRecordEstimateAuth, setAuthType as setRecordAuthType, setPatientInfo as setRecordPatientInfo, setRecordRequestPayment, setRecordRequestPaymentMethod, setRecordRequestAuthorization, setRecordRequestDate, setRecordRequestProvider, setRecordRequestRid, setRequestDetails as setRecordRequestDetails, setRecordRequestGovtId, setRecordRequestSupportingDocs, setCardNumber, setCardType } from 'store/actions/recordRequest.action';
import { setEstimateAuth as setFormEstimateAuth, setAuthType as setFormAuthType, setPatientInfo as setFormPatientInfo, setFormRequestPayment, setFormRequestPaymentMethod, setFormRequestAuthorization, setFormRequestFileData, setFormRequestProvider, setFormRequestRid, setFormRequestTypeId, setRequestDetails as setFormRequestDetails, setFormRequestForms, setFormRequestGovtId, setFormRequestSupportingDocs } from 'store/actions/formRequest.action';
import { recordRequestSelector, RecordRequestSteps } from 'store/reducers/recordRequest.reducer';
import { formRequestSelector, FormRequestSteps } from 'store/reducers/formRequest.reducer';
import { Parsers, URLtoFile } from 'utils';


type RequestCardProps = {
  data: RequestItem;
  className?: string;
  number: number;
  download?: boolean;
};

const recordWhiteList = ['/provider', '/patient', '/date', '/auth-type', '/authorization', '/govt-id', '/supporting-docs', '/request-info', '/payment', '/confirm']
const formWhitelist = ['/provider', '/patient', '/upload-type', '/auth-type', '/authorization', '/govt-id', '/supporting-docs', '/request-info', '/payment', '/confirm']

export const RequestCard: React.FC<RequestCardProps> = ({ data, className, download }) => {
  const { pushNewLinks } = useContext(HistoryContext);
  const authToken = cookies.load('authToken')
  const dispatch = useDispatch()
  const history = useHistory()
  const { is_subscription_deactive: isSubscriptionCancelled } = useSelector(subscriptionSelector).subscription

  const [isMounted, setMounted] = useState<boolean>(false)

  const requestDateOfService = {
    recordsAfter: data?.records_after && moment(data?.records_after).format(process.env.REACT_APP_DATE_FORMAT),
    recordsBefore: data?.records_before && moment(data?.records_before).format(process.env.REACT_APP_DATE_FORMAT),
    allRecords: !data?.records_after && !data?.records_before
  }
  const isForm = data.request_type == 'Form'
  const recordRequest = useSelector(recordRequestSelector)
  const formRequest = useSelector(formRequestSelector)
  const request = isForm ? formRequest : recordRequest

  let isDownloadable = false
  let updatedDate = moment(data[data.status == 'ordered' ? 'ordered_on' : 'completed_on'])
  if (moment().diff(updatedDate, 'days') <= 30 || !isSubscriptionCancelled) isDownloadable = true

  const getDateOfService = () => {
    let { allRecords, recordsAfter, recordsBefore } = requestDateOfService
    if (recordsAfter && recordsBefore) return `Between ${recordsAfter} - ${recordsBefore}`
    else if (recordsAfter) return `After ${recordsAfter}`
    else if (recordsBefore) return `Before ${recordsBefore}`
    else if (allRecords) return `All`
  }

  const setAuthorization = async () => {
    const generalReleaseData = data.authorizations?.find(item => !item.is_form_type && item.is_upload_authorization)
    const authorization = data.authorizations?.find(item => !item.is_form_type && !item.is_upload_authorization)
    const isEAuth = !authorization?.is_upload_authorization
    dispatch(isForm ? setFormAuthType(isEAuth ? 'PatientEAuthorization' : 'GeneralRelease') : setRecordAuthType(isEAuth ? 'PatientEAuthorization' : 'GeneralRelease'))
    let temp: any = {}
    if (authorization) {
      temp = {
        ...temp,
        fullName: authorization.auth_name,
        eauthId: authorization.id,
        initials: authorization.auth_initials,
        birthday: authorization.auth_dob,
        ssn: authorization.auth_ssn,
        iAmThe: authorization.auth_role,
        pdfData: { blobURL: authorization.url },
        signature: authorization.auth_signature && await URLtoFile(authorization.auth_signature),
        isDrawMode: authorization.draw_mode || false
      }
    }
    if (generalReleaseData) {
      temp = {
        ...temp,
        genId: generalReleaseData.id,
        generalReleaseFileURL: generalReleaseData.url,
        generalReleaseFileName: generalReleaseData.name
      }
    }
    dispatch(isForm ? setFormRequestAuthorization(temp) : setRecordRequestAuthorization(temp))
  }
  const setRequestInfo = () => {
    const { reason_for_request, record_types, delivery_method, fax_number, recipient_name, recipient_type, remarks  } = data
    let temp: RequestDetail = {
      deliveryMethod: delivery_method,
      reasonForRequest: reason_for_request && reason_for_request.split(',').map(item => ({ label: item, value: item })) || undefined,
      recordType: record_types?.length && record_types.map(item => ({ label: item, value: item })) || undefined,
      rushRequest : data.rush_request || undefined
    }
    let stepChange: boolean = temp.reasonForRequest?.length && temp.recordType?.length ? true : false
    if (delivery_method == 'fax-return') {
      temp.faxNumber = fax_number && Parsers.phone(fax_number)
      temp.recipientName = recipient_name
      temp.remark = remarks || ''
      if (recipient_type) temp.recipientType = { label: recipient_type, value: recipient_type }

      if (stepChange && (!temp.faxNumber || !temp.recipientName || !temp.recipientType)) stepChange = false
    }
    dispatch(isForm ? setFormRequestDetails(temp, stepChange) : setRecordRequestDetails(temp, stepChange))
  }

  useEffect(() => {
    if (request.rid) setTimeout(() => setMounted(true), 1000)
    else setMounted(true)
  }, [])
  useEffect(() => {
    if (isMounted && request.rid && data.rid && request.rid == data.rid) {
      const root = isForm ? '/form-request' : '/record-request'
      let wt = isForm ? formWhitelist : recordWhiteList
      let template = data.authorizations?.find(item => item.is_form_type) || {}
      if (isForm && template.form_id) wt[2] = '/type'
      let url = wt[request.currentStep - (request.currentStep == 8 ? 2 : 1)]
      setTimeout(() => {
        pushNewLinks(wt.slice(0, wt.indexOf(url) + 1).map(i => root + i))
        if (request.provider.remove_payment_method_for_free_requests && request.provider.free_pricing_strategy_enabled && url == '/payment') {
          history.push(`${root}/confirm`);
        } else {
          history.push(root + url);
        }
      }, 1000)
    }
  }, [request.rid, isMounted]);

  const requestClick = () => {
    if (data.status == 'draft') {
      const { custodian: provider, patient, authorizations, rid, card_id, initials, current_step, govtId, supporting_documents } = data

      const step = parseInt(current_step)

      dispatch(getContodianDetails({ name: provider.name, id: provider.id }, false, async (custodian) => {
        const patinentData = {
          patient_first_name: patient?.first_name,
          patient_middle_name: patient?.middle_name,
          patient_last_name: patient?.last_name,
          patient_dob: moment(patient?.dob).format(process.env.REACT_APP_DATE_FORMAT)
        }

        let temp: SupportingDocs[] = supporting_documents?.map((elem, i) => ({
          fileUrl: elem.url,
          fileName: elem.name,
          fileId: elem.id
        })) || [];

        let govtIdData = {
          frontFileURL: govtId?.front_file?.url,
          frontFileId: govtId?.front_file?.id,
          frontFileName: govtId?.front_file?.name,
          backFileURL: govtId?.back_file?.url,
          backFileId: govtId?.back_file?.id,
          backFileName: govtId?.back_file?.name,
        }
        if (isForm) {
          dispatch(setFormRequestProvider({ ...custodian, selectedLocation: data.selected_location }))
          if (step > FormRequestSteps.GovtId) dispatch(setFormRequestGovtId(govtIdData));
          if (step > FormRequestSteps.SupportingDocs) dispatch(setFormRequestSupportingDocs(temp));
          if (step > FormRequestSteps.Patient) dispatch(setFormPatientInfo(patinentData))
          dispatch(setFormRequestForms(custodian.patient_form_templates || []));
          if (step > FormRequestSteps.Type) {
            let template = authorizations?.find(item => item.is_form_type) || {}
            if (template.form_id) dispatch(setFormRequestTypeId(`${template.form_id}`, { form_record_url: template.url, form_name: template.name }));
            else dispatch(setFormRequestFileData({ form_record_url: template.url, form_name: template.name }))
          }
        } else {
          dispatch(setRecordRequestProvider(custodian))
          setRequestInfo()
          if (step > RecordRequestSteps.GovtId)  dispatch(setRecordRequestGovtId(govtIdData));
          if (step > RecordRequestSteps.SupportingDocs) dispatch(setRecordRequestSupportingDocs(temp));
          if (step > RecordRequestSteps.Patient) dispatch(setRecordPatientInfo(patinentData))
          if (step > RecordRequestSteps.Date) dispatch(setRecordRequestDate(requestDateOfService));
        }

        if (isForm ? step > FormRequestSteps.Authorization : step > RecordRequestSteps.Authorization) {
          await setAuthorization()
          setRequestInfo()
        }

        if (card_id && initials) {
          if (isForm && step > FormRequestSteps.Payment) {
            dispatch(setFormRequestPayment(card_id))
            dispatch(setFormRequestPaymentMethod('credit_card'))
            dispatch(setFormEstimateAuth(initials))
          } else if (!isForm && step > RecordRequestSteps.Payment) {
            dispatch(setRecordRequestPayment(card_id))
            dispatch(setRecordRequestPaymentMethod('credit_card'))
            dispatch(setRecordEstimateAuth(initials))
            dispatch(setCardNumber(data.card_details.card_number))
            dispatch(setCardType(data.card_details.card_type))
          }
        }
        setImmediate(() => dispatch(isForm ? setFormRequestRid(rid) : setRecordRequestRid(rid)))
      }));

    } else {
      history.push(`/request-detail/${data.rid}?requestType=${data.request_type}`)
    }
  }

  return (
    <div className={clsx(styles.root, className)} onClick={requestClick} role='button'>
      <Topography.H4 className='row'>
        <div className='col-12 col-md-auto'>{data.request_type === 'Record' ? `Medical Record ${data.rid}` : `Form Request ${data.rid}`}</div>
        <div className='col-12 col-md-auto mt-2 mt-md-0'>
          <StatusMarker className={styles.status}>{(data.status == 'completed_archived' || data.status == 'closed_archived') ? data.status?.replace(/_/g, ' - ') : data.status?.replace(/_/g, ' ')}</StatusMarker>
        </div>
      </Topography.H4>
      <ul>
        <li>
          <span>Care Provider:</span>
          {data.custodian.name}
        </li>
        <li>
          <span>Patient:</span>
          {data.patient.name}
        </li>
        {data.custodian_form && (
          <li className={styles.txtele}>
            <span>Form:</span>
            {data.authorizations?.filter((item: any) => item.is_form_type)[0]?.name}
          </li>
        )}
        {data.request_type === 'Record' && (
          <li>
            <span>Dates of service:</span>
            {getDateOfService()}
            {/* {createAtDate} - {submitDate} */}
          </li>
        )}
      </ul>
      {download && data?.medical_records?.length ? <div className={clsx(styles.detail, !isDownloadable ? styles.disable : '')}>
        {isDownloadable ? <a href={`${process.env.REACT_APP_BASE_API_URL}/api/v2/medical_records/download_all?request_id=${data.rid}&authorization-token=${authToken}`} download={true} className={styles.detail}>DETAILS</a> : 'DOWNLOAD'}
      </div> : <div className={styles.detail}>DETAILS</div>}
    </div>
  );
};